import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ShieldAlert, 
  CheckCircle2, 
  AlertTriangle, 
  ArrowRight, 
  Shield, 
  Users 
} from 'lucide-react';
import { KanbanTask } from '@/types';

interface MaintenanceStatusProps {
  tasks: KanbanTask[];
  // 🛡️ Hybrid Scope Props
  currentUserId: string;
  currentHouseholdId: string | null;
  activeScope: 'PERSONAL' | 'SHARED' | 'BOTH';
}

/**
 * 🛠️ MaintenanceStatus Widget
 * Refactored for Hybrid Scoping: Merges Personal and Shared Vitality streams.
 * Emerald = Private Partition | Indigo = Shared Hub
 */
export const MaintenanceStatus: React.FC<MaintenanceStatusProps> = ({ 
  tasks,
  currentUserId,
  currentHouseholdId,
  activeScope
}) => {
  const { t } = useTranslation();

  /**
   * 🛡️ HYBRID FILTERING
   * Filters maintenance tasks based on tenancy and scope.
   */
  const maintenanceItems = tasks.filter((task) => {
    const isMaint = (task.templateType === 'maintenance' || task.tags?.includes('maintenance')) && task.status !== 'history';
    if (!isMaint) return false;

    // 1. Personal Partition: Only show if user owns it and scope is personal/both
    if (task.scope === 'personal') {
      return task.ownerId === currentUserId && (activeScope === 'PERSONAL' || activeScope === 'BOTH');
    }

    // 2. Household Hub: Only show if household is active and scope is household/both
    if (task.scope === 'household') {
      return !!currentHouseholdId && (activeScope === 'SHARED' || activeScope === 'BOTH');
    }

    return false;
  });

  /**
   * 🧠 Health Calculation Logic
   */
  const calculateHealth = (task: KanbanTask) => {
    if (!task.recurrence?.isEnabled) return 100;

    const lastReset = new Date((task.recurrence as any).lastResetDate || task.completedAt || task.localChangeTimestamp);
    const daysSince = Math.floor((Date.now() - lastReset.getTime()) / (1000 * 60 * 60 * 24));
    
    const interval = task.recurrence.interval || 1;
    let freqDays = 30;
    if (task.recurrence.frequency === 'years') freqDays = 365;
    if (task.recurrence.frequency === 'weeks') freqDays = 7;
    if (task.recurrence.frequency === 'days') freqDays = 1;

    const totalCycleDays = interval * freqDays;
    return Math.max(0, Math.min(100, Math.round(((totalCycleDays - daysSince) / totalCycleDays) * 100)));
  };

  const criticalAssets = maintenanceItems
    .map(task => ({
      id: task.id,
      name: task.title,
      health: calculateHealth(task),
      isUrgent: task.priorityTier === 'P1',
      source: task.source,
      scope: task.scope
    }))
    .sort((a, b) => a.health - b.health)
    .slice(0, 4);

  return (
    <div className="flex flex-col h-full space-y-5">
      {/* --- WIDGET HEADER --- */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${
            activeScope === 'PERSONAL' || !currentHouseholdId ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600'
          }`}>
            <ShieldAlert className="w-3.5 h-3.5" />
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {currentHouseholdId ? t('dash.vitality.hybrid', 'System Vitality') : t('dash.vitality.personal', 'Personal Vitality')}
            </span>
            <div className="flex items-center gap-1 opacity-40">
              {activeScope === 'PERSONAL' || !currentHouseholdId ? <Shield className="w-2 h-2" /> : <Users className="w-2 h-2" />}
              <span className="text-[7px] font-bold uppercase tracking-tighter">{activeScope}</span>
            </div>
          </div>
        </div>
        {criticalAssets.some(a => a.health < 20) && (
          <div className="flex items-center gap-1 text-rose-500 animate-pulse">
            <AlertTriangle className="w-3 h-3" />
            <span className="text-[8px] font-black uppercase">Critical</span>
          </div>
        )}
      </div>

      {/* --- ASSET LIST --- */}
      <div className="flex-1 space-y-5 overflow-y-auto no-scrollbar">
        {criticalAssets.length > 0 ? (
          criticalAssets.map((asset) => (
            <div key={asset.id} className="space-y-2 group cursor-pointer">
              <div className="flex justify-between items-end">
                <div className="flex items-center gap-1.5 max-w-[70%]">
                  {asset.scope === 'personal' && <Shield className="w-2.5 h-2.5 text-emerald-500" />}
                  <h4 className={`text-[11px] font-bold uppercase italic tracking-tighter truncate ${
                    asset.scope === 'personal' ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-200'
                  }`}>
                    {asset.name}
                  </h4>
                </div>
                <span className={`text-[10px] font-black ${
                  asset.health < 30 ? 'text-rose-500' : asset.scope === 'personal' ? 'text-emerald-500' : 'text-indigo-500'
                }`}>
                  {asset.health}%
                </span>
              </div>
              <div className="relative h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-1000 ease-out ${
                    asset.health < 30 
                      ? 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.4)]' 
                      : asset.scope === 'personal' ? 'bg-emerald-500' : 'bg-indigo-500'
                  }`}
                  style={{ width: `${asset.health}%` }}
                />
              </div>
            </div>
          ))
        ) : (
          <div className="h-full flex flex-col items-center justify-center py-6 opacity-30 text-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mb-2" />
            <p className="text-[9px] font-black uppercase tracking-widest">All Systems Operational</p>
          </div>
        )}
      </div>

      {/* --- ACTIONS --- */}
      <button className="w-full py-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center justify-center gap-2 hover:bg-white dark:hover:bg-slate-800 transition-all group">
        <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 group-hover:text-indigo-500">
          {t('dash.view_ledger', 'View Full Ledger')}
        </span>
        <ArrowRight className="w-3 h-3 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
      </button>
    </div>
  );
};